/**
 * /marketplace/upcoming/:id — Public detail for a planned breeding /
 * expected litter. Buyers see parents + photos + COI + refund policy +
 * contract preview + waitlist count, then pay a deposit (Stripe Connect
 * destination charge → goes directly to breeder), and once paid, sign
 * the breeder's contract.
 */
import React, { useEffect, useState, useCallback } from "react";
import { webOrigin } from "@/lib/nativeBridge";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ReportButton from "@/components/ReportButton";
import { formatApiError, photoUrl } from "@/lib/utils-app";
import { useAuth } from "@/contexts/AuthContext";


const DOLLARS = (cents) => (cents == null ? "—" : `$${(cents / 100).toFixed(0)}`);

export default function MarketplaceUpcomingDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [contract, setContract] = useState(null);
  const [myDeposit, setMyDeposit] = useState(null);
  const [mySig, setMySig] = useState(null);
  const [legalName, setLegalName] = useState("");
  const [ackVenue, setAckVenue] = useState(false);
  const [ackRefund, setAckRefund] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/public/upcoming-breedings/${id}`);
      setDoc(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    if (user) {
      try {
        const c = await api.get(`/upcoming-breedings/${id}/contract`);
        setContract(c.data || null);
      } catch {
        setContract(null);
      }
      try {
        const d = await api.get("/upcoming-breedings/me/deposits");
        const mine = (d.data || []).find((x) => x.upcoming_breeding_id === id);
        setMyDeposit(mine || null);
      } catch {
        /* ignore */
      }
      try {
        const s = await api.get(`/upcoming-breedings/${id}/contract/my-signature`);
        setMySig(s.data || null);
      } catch {
        setMySig(null);
      }
    }
  }, [id, user]);

  useEffect(() => {
    load();
  }, [load]);

  // Returning from deposit checkout.
  useEffect(() => {
    const session = params.get("session_id");
    if (!session || params.get("deposit") !== "ok") return;
    api
      .get(`/upcoming-breedings/${id}/deposit-status/${session}`)
      .then((r) => {
        if (r.data.status === "paid") {
          toast.success("Deposit paid — you're on the waitlist!");
          load();
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, id]);

  const payDeposit = async () => {
    if (!user) {
      toast.error("Sign in first.");
      return nav(`/login?next=/marketplace/upcoming/${id}`);
    }
    if (!ackVenue || !ackRefund) {
      return toast.error("Please acknowledge both disclosures.");
    }
    if (!legalName.trim()) return toast.error("Enter your full legal name.");
    setBusy(true);
    try {
      const r = await api.post(`/upcoming-breedings/${id}/deposit`, {
        legal_name: legalName.trim(),
        venue_disclaimer_acknowledged: true,
        refund_policy_acknowledged: true,
        origin_url: webOrigin(),
      });
      window.location.assign(r.data.checkout_url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const requestRefund = async () => {
    if (!myDeposit) return;
    const reason = window.prompt("Optional — share why you're requesting a refund:");
    if (reason === null) return;
    try {
      await api.post(`/upcoming-breedings/deposits/${myDeposit.id}/refund-request`, {
        reason,
      });
      toast.success(
        "Refund request sent to the breeder. They'll honor it per their published policy. Armstrong does not auto-refund deposits."
      );
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const signContract = async () => {
    if (!ackVenue || !legalName.trim()) {
      return toast.error("Legal name and venue acknowledgement required.");
    }
    try {
      await api.post(`/upcoming-breedings/${id}/contract/sign-buyer`, {
        legal_name: legalName.trim(),
        venue_disclaimer_acknowledged: true,
      });
      toast.success("Contract signed.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!doc) {
    return (
      <AppShell>
        <div className="max-w-3xl mx-auto px-4 py-10 text-sm text-[#6B6B6B]">Loading…</div>
      </AppShell>
    );
  }

  const photos = [
    doc.dam_snapshot?.photo_url,
    doc.sire_snapshot?.photo_url,
    ...(doc.photos || []).map((p) => p.url),
  ].filter(Boolean).map(photoUrl);

  return (
    <AppShell>
      <SeoHead
        title={
          doc.dam_snapshot?.name && doc.sire_snapshot?.name
            ? `${doc.dam_snapshot.name} × ${doc.sire_snapshot.name} · upcoming litter · Armstrong K9 Registry`
            : "Upcoming litter · Armstrong K9 Registry"
        }
        description={
          `Reserve a spot on this ${doc.breed || "purebred"} waitlist — `
          + `$${Math.round((doc.deposit_amount_cents || 0) / 100)} deposit. `
          + `${doc.kennel?.name || "Independent breeder"}.`
        }
        imageUrl={`${process.env.REACT_APP_BACKEND_URL || ""}/api/public/upcoming-breedings/${id}/share-card.png`}
        type="product"
      />
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold font-bold mb-2">
            Upcoming · {doc.stage}
          </p>
          <h1 className="font-spectral text-4xl tracking-tight">
            {doc.dam_snapshot?.name} × {doc.sire_snapshot?.name}
          </h1>
          <p className="text-sm text-[#6B6B6B] mt-1">
            {doc.breed} · {doc.kennel?.name}
            {doc.kennel?.verified && (
              <span className="ml-2 inline-block text-[9px] uppercase tracking-[0.2em] font-bold bg-gold text-ink px-1.5 py-0.5 rounded align-middle">
                Verified
              </span>
            )}
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-2">
          {photos.slice(0, 6).map((url, i) => (
            <img key={i} src={url} alt="" className="aspect-square object-cover rounded" />
          ))}
          {photos.length === 0 && (
            <div className="aspect-square bg-[#FAFAFA] rounded flex items-center justify-center text-[11px] uppercase tracking-[0.2em] text-[#9B9B9B] sm:col-span-3">
              no photos yet
            </div>
          )}
        </div>

        <div className="grid sm:grid-cols-3 gap-4 text-sm">
          <Stat label="Price / puppy" v={DOLLARS(doc.price_per_puppy_cents)} />
          <Stat label="Deposit" v={DOLLARS(doc.deposit_amount_cents)} />
          <Stat label="Waitlist (paid)" v={doc.paid_deposits_count} />
          <Stat label="Expected whelp" v={doc.expected_whelp_date || "TBD"} />
          <Stat label="Earliest go-home" v={doc.earliest_go_home_date || "TBD"} />
          <Stat label="Est. puppies" v={doc.estimated_puppy_count ?? "—"} />
        </div>

        {doc.health_summary && (
          <Section title="Health testing & disclosures">
            <p className="text-sm whitespace-pre-wrap">{doc.health_summary}</p>
          </Section>
        )}

        <Section title="Refund policy (set by the breeder)">
          <p className="text-sm whitespace-pre-wrap text-[#3F3F3F]">
            {doc.refund_policy_text}
          </p>
          <p className="mt-2 text-[11px] text-[#6B6B6B] italic">
            Armstrong does not enforce, draft, or guarantee this policy. Deposits
            flow directly to the breeder.
          </p>
        </Section>

        {doc.additional_info && (
          <Section title="More from the breeder">
            <p className="text-sm whitespace-pre-wrap">{doc.additional_info}</p>          </Section>
        )}

        {doc.video_call_offer_price_cents > 0 && (
          <Section title="Schedule a video call">
            <p className="text-sm">
              This breeder offers paid video calls / kennel walkthroughs for{" "}
              <b>{DOLLARS(doc.video_call_offer_price_cents)}</b>.
            </p>
          </Section>
        )}

        {/* Venue disclaimer */}
        <div className="rounded-xl border border-[#E5BEC0] bg-[#FBEBEC] p-4">
          <p className="text-[11.5px] text-[#3F3F3F] leading-relaxed">
            <b>Armstrong K9 Registry is a venue, not a party to this contract.</b>{" "}
            {doc.venue_disclaimer_text}
          </p>
        </div>

        {/* Deposit form OR my-deposit status */}
        {!myDeposit && (
          <section className="rounded-xl border border-[#EAEAEA] bg-white p-5">
            <h2 className="font-spectral text-xl tracking-tight">Reserve your spot</h2>
            <p className="text-sm text-[#6B6B6B] mt-1">
              Pay a {DOLLARS(doc.deposit_amount_cents)} deposit to join the waitlist. Money
              flows directly to the breeder via Stripe Connect.
            </p>
            <div className="mt-3 space-y-3">
              <Input
                value={legalName}
                onChange={(e) => setLegalName(e.target.value)}
                placeholder="Your full legal name"
                data-testid="upcoming-deposit-legalname"
              />
              <label className="flex items-start gap-2 text-[12px] text-[#3F3F3F]">
                <input
                  type="checkbox"
                  checked={ackRefund}
                  onChange={(e) => setAckRefund(e.target.checked)}
                  data-testid="upcoming-deposit-ack-refund"
                />
                <span>I've read the breeder's refund policy above and agree to it.</span>
              </label>
              <label className="flex items-start gap-2 text-[12px] text-[#3F3F3F]">
                <input
                  type="checkbox"
                  checked={ackVenue}
                  onChange={(e) => setAckVenue(e.target.checked)}
                  data-testid="upcoming-deposit-ack-venue"
                />
                <span>
                  I acknowledge Armstrong K9 Registry is a venue, not a party to this transaction,
                  and the deposit flows directly to the breeder. Armstrong is not the
                  merchant of record for this deposit.
                </span>
              </label>
              <Button
                disabled={busy || !ackRefund || !ackVenue || !legalName.trim()}
                onClick={payDeposit}
                className="bg-ink hover:bg-crimson text-white rounded-full"
                data-testid="upcoming-deposit-btn"
              >
                Pay deposit & join waitlist — {DOLLARS(doc.deposit_amount_cents)}
              </Button>
            </div>
          </section>
        )}

        {myDeposit && (
          <section className="rounded-xl border border-[#3DA56E] bg-[#EAF7F0] p-5" data-testid="upcoming-my-position">
            <h2 className="font-spectral text-xl tracking-tight">
              You're #{myDeposit.waitlist_rank} on the waitlist
              {doc.paid_deposits_count ? (
                <span className="text-sm text-[#3F3F3F] font-normal ml-2">
                  of {doc.paid_deposits_count} paid {doc.paid_deposits_count === 1 ? "buyer" : "buyers"}
                </span>
              ) : null}
            </h2>
            <p className="text-sm text-[#3F3F3F] mt-1">
              {DOLLARS(myDeposit.amount_cents)} paid · status: {myDeposit.status}
            </p>
            {(doc.expected_whelp_date || doc.whelped_at) && (
              <p
                className="text-xs text-[#3F3F3F] mt-2 leading-relaxed"
                data-testid="upcoming-take-home-eta"
              >
                <strong>Estimated take-home:</strong>{" "}
                {(() => {
                  // 8 weeks after whelp (US legal minimum). If already
                  // whelped, count from that. Otherwise from expected.
                  const whelp = doc.whelped_at || doc.expected_whelp_date;
                  if (!whelp) return "TBD";
                  const d = new Date(whelp);
                  if (isNaN(d.getTime())) return "TBD";
                  d.setDate(d.getDate() + 56);
                  const week = d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
                  return `week of ${week} (8 weeks after whelp — US legal minimum)`;
                })()}
              </p>
            )}
            {myDeposit.assigned_dog_id && (
              <div
                className="mt-3 p-3 bg-white border border-[#3DA56E] rounded-lg"
                data-testid="upcoming-assigned-puppy"
              >
                <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#3DA56E]">
                  Your puppy
                </p>
                <p className="text-sm font-bold mt-1">
                  Reserved · the breeder has assigned a puppy to you. Sign the
                  contract below to lock in your pickup.
                </p>
              </div>
            )}
            {myDeposit.status === "paid" && (
              <Button
                variant="outline"
                size="sm"
                onClick={requestRefund}
                className="mt-3 rounded-full"
                data-testid="upcoming-refund-request-btn"
              >
                Request refund from breeder
              </Button>
            )}
          </section>
        )}

        {/* Contract preview + sign */}
        {myDeposit?.status === "paid" && contract?.pdf_url && (
          <section className="rounded-xl border border-[#EAEAEA] bg-white p-5">
            <h2 className="font-spectral text-xl tracking-tight">Sales contract</h2>
            <p className="text-sm mt-1">
              <a href={contract.pdf_url} target="_blank" rel="noreferrer" className="underline">
                Review the breeder's contract (PDF)
              </a>
            </p>
            {mySig ? (
              <p className="mt-2 text-sm text-[#3DA56E]">
                ✓ Signed on {new Date(mySig.timestamp).toLocaleDateString()} as{" "}
                {mySig.signer_legal_name}
              </p>
            ) : (
              <div className="mt-3 space-y-3">
                <Input
                  value={legalName}
                  onChange={(e) => setLegalName(e.target.value)}
                  placeholder="Your full legal name (must match deposit)"
                  data-testid="upcoming-sign-legalname"
                />
                <label className="flex items-start gap-2 text-[12px] text-[#3F3F3F]">
                  <input
                    type="checkbox"
                    checked={ackVenue}
                    onChange={(e) => setAckVenue(e.target.checked)}
                    data-testid="upcoming-sign-ack"
                  />
                  <span>
                    I've reviewed this contract and understand Armstrong K9 Registry is a venue, not
                    a party to it. I should consult independent counsel before signing.
                  </span>
                </label>
                <Button
                  onClick={signContract}
                  disabled={!ackVenue || !legalName.trim()}
                  className="bg-ink hover:bg-crimson text-white rounded-full"
                  data-testid="upcoming-sign-btn"
                >
                  Sign contract
                </Button>
              </div>
            )}
          </section>
        )}

        {/* Trust & safety — report bad listings */}
        <div className="flex justify-center pt-4 border-t border-[#EAEAEA]">
          <ReportButton
            targetType="upcoming_litter"
            targetId={id}
            targetLabel={
              doc.dam_snapshot?.name && doc.sire_snapshot?.name
                ? `${doc.dam_snapshot.name} × ${doc.sire_snapshot.name}`
                : "this upcoming litter"
            }
            testIdPrefix="upcoming-report"
          />
        </div>
      </div>
    </AppShell>
  );
}

function Stat({ label, v }) {
  return (
    <div className="bg-[#FAFAFA] border border-[#EAEAEA] rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#6B6B6B]">
        {label}
      </div>
      <div className="text-lg font-spectral tracking-tight mt-1">{v}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="rounded-xl border border-[#EAEAEA] bg-white p-5">
      <h2 className="font-spectral text-xl tracking-tight">{title}</h2>
      <div className="mt-2">{children}</div>
    </section>
  );
}
